import React, { useState } from 'react';
import {
    Business,
    Store,
    PointOfSale,
    AdminPanelSettings,
    ArrowBack,
    Security,
    Dashboard,
    Receipt,
    CheckCircle,
    Add,
    Close,
    People,
    CalendarToday
} from '@mui/icons-material';
import OrganizationCreation from './OrganizationCreation';
import { hosts } from '../../config/environment';
import {
    getOrganizationRoleKeys,
    getRoleLabel,
    normalizeRoleKey,
    organizationNeedsRoleChoice,
} from '../../utils/organizationRoleUtils';

const getOrgLogoUrl = (orgId) => {
    const base = (hosts.api || '').replace(/\/$/, '');
    return base ? `${base}/exposed/v1/mda/images/show?target=logo&target_id=${orgId}` : '';
};

const OrganizationSelection = ({ user, onOrganizationSelect, onBack }) => {
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [selectedRole, setSelectedRole] = useState('');
    const [hoveredOrg, setHoveredOrg] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const handleSelect = (org) => {
        setSelectedOrganization(org);
        if (organizationNeedsRoleChoice(org)) {
            setSelectedRole('');
        } else {
            const finalRole = normalizeRoleKey(org.role);
            setSelectedRole(org.role);
            if (finalRole && onOrganizationSelect) {
                onOrganizationSelect(org.id, finalRole, org);
            }
        }
    };

    const handleRoleClick = (role) => {
        if (!selectedOrganization) return;
        const normalized = normalizeRoleKey(role);
        setSelectedRole(role);
        if (normalized && onOrganizationSelect) {
            onOrganizationSelect(selectedOrganization.id, normalized, selectedOrganization);
        }
    };

    const handleConfirm = () => {
        if (selectedOrganization) {
            let finalRole = organizationNeedsRoleChoice(selectedOrganization)
                ? selectedRole
                : selectedOrganization.role;
            finalRole = normalizeRoleKey(finalRole);
            if (finalRole && onOrganizationSelect) {
                onOrganizationSelect(selectedOrganization.id, finalRole, selectedOrganization);
            }
        }
    }

    const handleOrganizationCreated = (newOrganization) => {
        setShowCreateModal(false);
        setSelectedOrganization(newOrganization);
        setSelectedRole('admin');

        // Ajouter la nouvelle organisation à la liste des organisations de l'utilisateur
        if (user?.organizations) {
            user.organizations = [newOrganization, ...user.organizations];
        }

        // Appeler immédiatement onOrganizationSelect pour naviguer
        // Pas besoin de setTimeout, navigation directe
        if (onOrganizationSelect) {
            onOrganizationSelect(newOrganization.id, 'admin', newOrganization);
        }
    }

    const getRoleConfig = (role) => {
        const configs = {
            admin: {
                label: 'Administrateur',
                icon: AdminPanelSettings,
                gradient: 'from-purple-500 to-purple-600',
                bgGradient: 'from-purple-50 to-purple-100',
                borderColor: 'border-purple-200',
                textColor: 'text-purple-700',
                badgeColor: 'bg-purple-100 text-purple-800',
                description: 'Accès complet à la gestion et configuration'
            },
            cashier: {
                label: 'Caissier',
                icon: PointOfSale,
                gradient: 'from-blue-500 to-blue-600',
                bgGradient: 'from-blue-50 to-blue-100',
                borderColor: 'border-blue-200',
                textColor: 'text-blue-700',
                badgeColor: 'bg-blue-100 text-blue-800',
                description: 'Interface de point de vente et transactions'
            },
            both: {
                label: 'Administrateur & Caissier',
                icon: Business,
                gradient: 'from-amber-500 to-amber-600',
                bgGradient: 'from-amber-50 to-amber-100',
                borderColor: 'border-amber-200',
                textColor: 'text-amber-700',
                badgeColor: 'bg-amber-100 text-amber-800',
                description: 'Accès complet à toutes les fonctionnalités'
            }
        }
        return configs[role] || {
            label: 'Utilisateur',
            icon: Store,
            gradient: 'from-gray-500 to-gray-600',
            bgGradient: 'from-gray-50 to-gray-100',
            borderColor: 'border-gray-200',
            textColor: 'text-gray-700',
            badgeColor: 'bg-gray-100 text-gray-800',
            description: 'Accès standard'
        }
    }

    const getCategoryLabel = (category) => {
        const categories = {
            retail: { label: 'Commerce de détail', emoji: '🏪' },
            restaurant: { label: 'Restauration', emoji: '🍽️' },
            service: { label: 'Service', emoji: '🔧' },
            ecommerce: { label: 'E-commerce', emoji: '🛒' },
            other: { label: 'Autre', emoji: '🏢' }
        }
        return categories[category] || { label: 'Autre', emoji: '🏢' }
    }

    const canConfirm = selectedOrganization &&
        (selectedOrganization.role !== 'both' || (selectedOrganization.role === 'both' && selectedRole));

    return (
        <>
            <div className="min-h-screen bg-slate-50 flex flex-col">
                {/* Hero header */}
                <header className="bg-slate-900 text-white px-6 py-8 shadow-xl">
                    <div className="max-w-6xl mx-auto">
                        <button
                            onClick={onBack}
                            className="inline-flex items-center text-slate-300 hover:text-white mb-8 transition-colors text-sm font-medium"
                        >
                            <ArrowBack className="mr-2" />
                            Retour à la connexion
                        </button>
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div>
                                <p className="text-slate-400 text-sm uppercase tracking-widest mb-1">Choisissez votre espace</p>
                                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Sélection de l&apos;organisation</h1>
                                <p className="mt-2 text-slate-300">
                                    Bon retour, <span className="font-semibold text-amber-400">{user.username}</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-slate-700 flex items-center justify-center">
                                    <Business className="text-amber-400 text-2xl" />
                                </div>
                                <div className="text-right">
                                    <p className="text-slate-400 text-xs uppercase tracking-wider">Session active</p>
                                    <p className="text-white font-medium">Connexion sécurisée</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <div className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Organizations List */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
                                <div className="px-8 py-6 border-b border-slate-100 flex flex-wrap items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                                            <Store className="text-slate-600" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-semibold text-slate-900">Vos organisations</h2>
                                            <p className="text-slate-500 text-sm">Sélectionnez l&apos;organisation avec laquelle continuer</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowCreateModal(true)}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors shadow-sm"
                                    >
                                        <Add className="text-lg" />
                                        Nouvelle organisation
                                    </button>
                                </div>

                                <div className="p-6 space-y-3">
                                    {user.organizations.map((org) => {
                                        const roleConfig = getRoleConfig(org.role);
                                        const categoryInfo = getCategoryLabel(org.category);
                                        const RoleIcon = roleConfig.icon;
                                        const isSelected = selectedOrganization?.id === org.id;
                                        const isHovered = hoveredOrg === org.id;
                                        const hasMultipleRoles = organizationNeedsRoleChoice(org);
                                        const roleKeys = getOrganizationRoleKeys(org);
                                        const logoUrl = getOrgLogoUrl(org.id);

                                        return (
                                            <div
                                                key={org.id}
                                                onClick={() => handleSelect(org)}
                                                onMouseEnter={() => setHoveredOrg(org.id)}
                                                onMouseLeave={() => setHoveredOrg(null)}
                                                className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                                    isSelected
                                                        ? 'border-amber-500 bg-amber-50/50 shadow-md'
                                                        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                                                } ${isHovered && !isSelected ? 'border-slate-300' : ''}`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    {/* Organization Logo (API or initial) */}
                                                    <div className="flex-shrink-0">
                                                        <div className={`relative w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden text-lg font-bold ${
                                                            isSelected ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600'
                                                        }`}>
                                                            {logoUrl ? (
                                                                <img
                                                                    src={logoUrl}
                                                                    alt=""
                                                                    className="absolute inset-0 w-full h-full object-cover"
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            ) : null}
                                                            <span className="relative z-0">{(org.name || 'O').charAt(0).toUpperCase()}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                                            <h3 className={`text-lg font-semibold ${isSelected ? 'text-amber-900' : 'text-slate-900'}`}>
                                                                {org.name}
                                                            </h3>
                                                            {hasMultipleRoles && (
                                                                <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600">
                                                                    Double rôle
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500">
                                                            <span className="flex items-center gap-1">
                                                                <RoleIcon className="w-4 h-4 text-slate-400" />
                                                                {roleConfig.label}
                                                            </span>
                                                            <span>{categoryInfo.emoji} {categoryInfo.label}</span>
                                                        </div>
                                                        {roleKeys.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                                                {(org.current_user_roles || []).map((r) => (
                                                                    <span
                                                                        key={r.id || r.name}
                                                                        className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                                                    >
                                                                        {r.name || getRoleLabel(normalizeRoleKey(r))}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {(org.users != null || org.createdAt) && (
                                                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                                                                {org.users != null && (
                                                                    <span className="flex items-center gap-1">
                                                                        <People className="w-3.5 h-3.5" />
                                                                        {org.users} utilisateur(s)
                                                                    </span>
                                                                )}
                                                                {org.createdAt && (
                                                                    <span className="flex items-center gap-1">
                                                                        <CalendarToday className="w-3.5 h-3.5" />
                                                                        {org.createdAt}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                                        isSelected ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-300'
                                                    }`}>
                                                        {isSelected && <CheckCircle className="w-4 h-4" />}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {user.organizations.length === 0 && (
                                        <div className="text-center py-14 px-4">
                                            <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                                <Business className="text-slate-400 text-3xl" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune organisation</h3>
                                            <p className="text-slate-500 mb-6 text-sm">Créez votre première organisation pour commencer.</p>
                                            <button
                                                onClick={() => setShowCreateModal(true)}
                                                className="inline-flex items-center gap-2 bg-amber-500 text-white px-5 py-2.5 rounded-xl font-medium hover:bg-amber-600 transition-colors"
                                            >
                                                <Add className="text-lg" />
                                                Créer une organisation
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {selectedOrganization && (
                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
                                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                                        <Business className="text-amber-600 w-5 h-5" />
                                        <h3 className="font-semibold text-slate-900">Organisation sélectionnée</h3>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="relative w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-slate-100 text-slate-600 font-bold text-lg">
                                                {getOrgLogoUrl(selectedOrganization.id) ? (
                                                    <img
                                                        src={getOrgLogoUrl(selectedOrganization.id)}
                                                        alt=""
                                                        className="absolute inset-0 w-full h-full object-cover"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : null}
                                                <span className="relative z-0">{(selectedOrganization.name || 'O').charAt(0).toUpperCase()}</span>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-slate-900">{selectedOrganization.name}</h4>
                                                <p className="text-sm text-slate-500">{getCategoryLabel(selectedOrganization.category).label}</p>
                                            </div>
                                        </div>

                                    {selectedOrganization.role === 'both' && (
                                        <div className="pt-4 border-t border-slate-100">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Security className="text-slate-500 w-4 h-4" />
                                                <h4 className="font-medium text-slate-900">Choisir le rôle</h4>
                                            </div>
                                            <p className="text-slate-500 text-sm mb-3">Mode d&apos;accès pour cette session</p>
                                            <div className="space-y-2">
                                                {[
                                                    { role: 'admin', icon: Dashboard, label: 'Administrateur', description: 'Gestion complète' },
                                                    { role: 'cashier', icon: Receipt, label: 'Caissier', description: 'Interface caisse' }
                                                ].map(({ role, icon: Icon, label, description }) => (
                                                    <div
                                                        key={role}
                                                        onClick={() => handleRoleClick(role)}
                                                        className={`p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                                                            selectedRole === role
                                                                ? 'border-amber-500 bg-amber-50'
                                                                : 'border-slate-200 hover:border-slate-300'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`p-1.5 rounded-lg ${selectedRole === role ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                                                                    <Icon className="w-4 h-4" />
                                                                </div>
                                                                <div>
                                                                    <div className="font-medium text-slate-900">{label}</div>
                                                                    <div className="text-xs text-slate-500">{description}</div>
                                                                </div>
                                                            </div>
                                                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                                                selectedRole === role ? 'bg-amber-500 border-amber-500' : 'border-slate-300'
                                                            }`}>
                                                                {selectedRole === role && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            {!selectedRole && (
                                                <p className="mt-2 text-amber-600 text-sm">Sélectionnez un rôle pour continuer</p>
                                            )}
                                        </div>
                                    )}

                                    {selectedOrganization.role !== 'both' && (
                                        <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Security className="text-slate-500 w-4 h-4" />
                                                <span className="font-medium text-slate-900">Rôle</span>
                                            </div>
                                            <span className={`px-2.5 py-1 rounded-lg text-sm font-medium ${getRoleConfig(selectedOrganization.role).badgeColor}`}>
                                                {getRoleConfig(selectedOrganization.role).label}
                                            </span>
                                        </div>
                                    )}
                                    {/* Plus de bouton “Accéder à” : la navigation se fait dès le choix org/rôle */}
                                </div>
                                </div>
                            )}

                            {!selectedOrganization && (
                                <div className="bg-slate-800 rounded-2xl p-6 text-white">
                                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                                        <PointOfSale className="text-amber-400" />
                                        Accès
                                    </h3>
                                    <div className="space-y-2 text-sm text-slate-300">
                                        <div className="flex justify-between"><span>Organisations</span><span className="font-medium text-white">{user.organizations.length}</span></div>
                                        <div className="flex justify-between"><span>Multi-rôles</span><span className="font-medium text-white">{user.organizations.filter(o => o.role === 'both').length}</span></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showCreateModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between px-8 py-5 border-b border-slate-200 bg-white">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                                    <Business className="text-slate-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Créer une organisation</h2>
                                    <p className="text-slate-500 text-sm">Configurez votre nouvelle organisation</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                            >
                                <Close className="text-xl" />
                            </button>
                        </div>
                        <div className="max-h-[calc(90vh-80px)] overflow-y-auto">
                            <OrganizationCreation
                                onOrganizationCreated={handleOrganizationCreated}
                                onBack={() => setShowCreateModal(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default OrganizationSelection